import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Row, Col, Collapse, Form, FormGroup, Input, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import AutoGrowTextArea from 'components/layout/forms/AutoGrowTextArea';
import DatePicker from 'components/layout/forms/DatePicker';
import TextWithDateInputField from 'components/layout/forms/TextWithDateInputField';
import maximize from 'assets/maximize.svg';
import minimize from 'assets/minimize.svg';
import NumberInputField from 'components/layout/forms/NumberInputField';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import SearchDropdown from 'components/layout/forms/SearchDropdown';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import SelectInputTranslated from 'components/layout/forms/SelectInputTranslated';
import TextInputField from 'components/layout/forms/TextInputField';
import translations from 'services/translations/translations';
import UpdatedField from 'components/layout/forms/UpdatedField';
import UserSelect from 'components/layout/forms/UserSelect';
import {
  APPROVED,
  OPEN,
  READY_TO_SEND,
  SENT,
  isConvertedStatus,
} from 'services/enums/offers/Statuses';
import { OPPORTUNITY } from 'services/enums/offers/offerTypes';
import { NO } from 'services/enums/offers/collateralTypes';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_NAMES,
  SUPPORTED_LANGUAGES,
  isSupportedLanguage,
} from 'services/translations/languages';

import {
  BUILDING_TYPE,
  COLLATERAL,
  CONTACT_ROLE,
  CONTRACT_TYPE,
  EMPLOYEE_INFORMATION,
  WAY_OF_RECEIVING,
  WORK_TYPE,
  toOptions,
  useChoiceList,
} from 'services/offers/offerChoices';

import { showDesigners } from 'components/offers/OfferSelectOptions';
import OfferInfoRowFields from './OfferInfoRowFields';

import IOffer from './IOffer';
import OfferInvoicingInfo from './OfferInvoicingInfo';
import OfferTextField from './OfferTextField';
import Status from './fields/Status';

import 'style/layout/Icons/CollapseIcon.css';
import 'style/offers/OfferBasicInfo.css';

// The phases of the building project of a sales opportunity (backend ProjectPhases)
const PROJECT_PHASES = ['project-planning', 'site-planning'];

// The statuses still waiting for an answer, shown in green
const OPEN_STATUSES = [OPEN, READY_TO_SEND, SENT];

// Additional offers are a separate kind of offer and cannot be changed to or from
const CHANGEABLE_OFFER_TYPES = [
  'offer',
  'preliminary-offer',
  'material-offer',
  'cost-estimate',
];

const OfferBasicInfo = ({
  actions,
  statusBarTarget,
  toggleOffer,
  offerIsOpen,
  formState,
  handleFormState,
  customerOffice,
  handleCustomerOffice,
  searchCustomerOffice,
  startDate,
  onChangeDate,
  endDate,
  siteEndDate,
  siteStartDate,
  offerId,
  requestedSubmissionDate,
  projectContacts,
  saveOffer,
  customers,
  handlePaymentTerm,
  paymentTerm,
  initialStatus,
  locked,
  organizationUnitField,
  readOnly,
  typeCanBeChanged,
  additionalRecipients,
  signatureUser,
  handleSignatureUser,
}: {
  // The history and NDA buttons, on the line of the first titles
  actions?: JSX.Element | null;
  // Of a sales opportunity: where its line of the save button goes (the bottom of the page)
  statusBarTarget?: HTMLElement | null;
  toggleOffer: any;
  offerIsOpen: boolean;
  formState: IOffer;
  handleFormState: any;
  customerOffice: any;
  handleCustomerOffice: any;
  searchCustomerOffice: any;
  startDate: any;
  onChangeDate: any;
  endDate: any;
  // The start and end of the whole building project, null when not known
  siteEndDate: Date | null;
  siteStartDate: Date | null;
  // Undefined until the offer is saved
  offerId?: number;
  // The date the customer wishes to have the offer by
  requestedSubmissionDate: Date | null;
  // The contacts of the project besides the customers (e.g. architects), rows with roles
  projectContacts?: JSX.Element | null;
  saveOffer: any;
  customers: any;
  handlePaymentTerm: any;
  paymentTerm: any;
  initialStatus: null | string;
  locked: boolean;
  // Choosing the department of the offer, the department is only shown without it
  organizationUnitField?: JSX.Element | null;
  // The user can only read the offers of the department, not even change the status
  readOnly?: boolean;
  typeCanBeChanged: boolean;
  additionalRecipients?: JSX.Element | null;
  signatureUser: any;
  handleSignatureUser: any;
}): JSX.Element | null => {
  const { t, i18n } = useTranslation([
    translations.offers,
    translations.common,
    translations.products,
    translations.userManagement,
  ]);

  // A new offer is printed in the language of the user unless another one is chosen
  const getNewOfferLanguage = () =>
    isSupportedLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;

  // The choices the admins manage, in their order; a hidden one only when it is chosen
  const buildingTypes = useChoiceList(BUILDING_TYPE, formState.buildingType);
  const collateralTypes = useChoiceList(COLLATERAL, formState.collateral);
  const contractTypes = useChoiceList(CONTRACT_TYPE, formState.contractType);
  const employeeInformationTypes = useChoiceList(
    EMPLOYEE_INFORMATION,
    formState.employeeInformationType
  );
  const wayOfReceivingTypes = useChoiceList(
    WAY_OF_RECEIVING,
    formState.wayOfReceivingType
  );
  const workTypes = useChoiceList(WORK_TYPE, formState.jobType);
  const customerRoles = useChoiceList(CONTACT_ROLE, formState.customerRole);

  // An approved offer and a converted opportunity are final
  const offerIsApproved = () =>
    initialStatus === APPROVED || isConvertedStatus(initialStatus);

  const offerIsLocked = () => locked || readOnly === true;

  // Whether the user has tried to save the offer: then the empty fields stand out
  const [saveAttempted, setSaveAttempted] = useState(false);

  // A sales opportunity: basic information, dates and contacts only
  const isOpportunity = formState.offerType === OPPORTUNITY;

  const noCollateral = () => formState.collateral === NO;

  // After a save the empty fields stand out; of an opportunity only the required target
  // and signer
  let emptyFieldsClass: string | undefined;
  if (saveAttempted) {
    emptyFieldsClass = isOpportunity
      ? 'show-empty-required'
      : 'show-empty-fields';
  }

  // The times of the offer (created, updated, sent, ordered) in the schedule with the
  // dates: the date in the field, the time and the name of the user in its tooltip
  const timeFields = (
    <>
      <UpdatedField
        compact
        label={t(`${translations.common}:Created`)}
        updated={formState.created}
        updatedUser={formState.createdUser}
      />
      {/* A sales opportunity is not sent or ordered: in their places the days it was
          saved in the phases of project planning and site planning, then the update */}
      {isOpportunity ? (
        <>
          <UpdatedField
            compact
            label={t(
              `${translations.offers}:projectPhase.phases.project-planning`
            )}
            updated={formState.projectPlanningAt ?? null}
            updatedUser={null}
          />
          <UpdatedField
            compact
            label={t(
              `${translations.offers}:projectPhase.phases.site-planning`
            )}
            updated={formState.sitePlanningAt ?? null}
            updatedUser={null}
          />
          <UpdatedField
            compact
            label={t(`${translations.common}:Updated`)}
            updated={formState.updated}
            updatedUser={formState.updatedUser}
          />
        </>
      ) : (
        <>
          <UpdatedField
            compact
            label={t(`${translations.common}:Updated`)}
            updated={formState.updated}
            updatedUser={formState.updatedUser}
          />
          <UpdatedField
            compact
            label={t(`${translations.offers}:Sent`)}
            updated={formState.sentAt}
            updatedUser={formState.sentUser}
          />
          <UpdatedField
            compact
            label={t(`${translations.offers}:statusLine.Ordered`)}
            updated={formState.orderedAt ?? null}
            updatedUser={formState.orderedUser ?? null}
          />
        </>
      )}
    </>
  );

  // The department, the signer and the status on the line of the save button, the status
  // right beside it (statusBar below)
  const statusFields = (
    <>
      {/* The department of the offer beside the signer */}
      <Col className="offer-pdf-field offer-status-department">
        {organizationUnitField ?? (
          <TextInputField
            disabled
            label={t(`${translations.userManagement}:Organization unit`)}
            required
            value={
              formState.organizationUnit === null
                ? ''
                : formState.organizationUnit.name
            }
          />
        )}
      </Col>
      {/* The signer of the PDF beside the status */}
      <Col className="offer-pdf-field offer-status-signature">
        <UserSelect
          disabled={offerIsLocked()}
          label={t(`${translations.offers}:Signature`)}
          onChange={handleSignatureUser}
          ownOrganizationUnit
          value={signatureUser}
        />
      </Col>
      <Col
        className={`offer-pdf-field offer-status-select ${
          OPEN_STATUSES.includes(formState.status) ? 'offer-status-open' : ''
        }`}
      >
        <Status
          disabled={readOnly}
          formState={formState}
          handleFormState={handleFormState}
          initialStatus={initialStatus}
          offerId={offerId}
          offerType={formState.offerType}
          sent={formState.sentAt !== null}
        />
      </Col>
    </>
  );

  // Of a sales opportunity: whether the building project is still being planned as a
  // project or its site is being designed
  const projectPhaseColumn = (
    <Col className="offer-info-column" md={6}>
      <h4 className="offer-info-column-title">
        {t(`${translations.offers}:projectPhase.Title`)}
      </h4>
      <SelectInputFieldControlled
        value={formState.projectPhase ?? ''}
        label={t(`${translations.offers}:projectPhase.Phase`)}
        id="projectPhase"
        name="projectPhase"
        onChange={handleFormState}
        options={[
          <option key="" value="">
            -
          </option>,
          ...PROJECT_PHASES.map((phase) => (
            <option key={phase} value={phase}>
              {t(`${translations.offers}:projectPhase.phases.${phase}`)}
            </option>
          )),
        ]}
        disabled={offerIsLocked()}
      />
    </Col>
  );

  const offerColumn = (
    <Col className="offer-info-column" md={6}>
      <h4 className="offer-info-column-title">
        {t(`${translations.offers}:basicInfoColumns.Offer`)}
      </h4>
      <SelectInputTranslated
        // An opportunity becomes an offer only by closing it as converted
        disabled={!typeCanBeChanged || isOpportunity}
        key="offerType"
        label={t(`${translations.offers}:Offer Type`)}
        name="offerType"
        onChange={handleFormState}
        options={isOpportunity ? [OPPORTUNITY] : CHANGEABLE_OFFER_TYPES}
        required
        translationPath={`${translations.offers}:types`}
        value={formState.offerType}
      />
      <FormGroup>
        <Label className="label" for="requestedSubmissionDate">
          {t(`${translations.offers}:Requested submission date`)}
        </Label>
        <DatePicker
          disabled={offerIsLocked()}
          id="requestedSubmissionDate"
          isClearable={!offerIsLocked()}
          onChange={(date) => onChangeDate(date, 'requestedSubmission')}
          selected={requestedSubmissionDate}
        />
      </FormGroup>
      {/* The standard texts of the PDF are printed in this language */}
      <FormGroup>
        <Label className="label" for="offerLanguage">
          {t(`${translations.offers}:Offer Language`)}
        </Label>
        <Input
          className="text-field"
          disabled={offerIsLocked()}
          id="offerLanguage"
          name="language"
          onChange={handleFormState}
          type="select"
          value={formState.language || getNewOfferLanguage()}
        >
          {SUPPORTED_LANGUAGES.map((language) => (
            <option key={language} value={language}>
              {LANGUAGE_NAMES[language]}
            </option>
          ))}
        </Input>
      </FormGroup>
      {isOpportunity ? null : (
        <>
          <TextInputField
            disabled={offerIsLocked()}
            value={formState.orderNumber}
            label={t(`${translations.offers}:Order Number`)}
            required
            id="orderNumber"
            name="orderNumber"
            onChange={handleFormState}
          />
          <TextInputField
            value={formState.customerMark}
            label={t(`${translations.offers}:customerMark`)}
            required
            id="customerMark"
            name="customerMark"
            onChange={handleFormState}
            disabled={offerIsLocked()}
          />
        </>
      )}
      {typeCanBeChanged && !isOpportunity ? (
        <p className="offer-type-info">
          {t(`${translations.offers}:Offer type info`)}
        </p>
      ) : null}
    </Col>
  );

  const contractColumn = (
    <Col className="offer-info-column" md={6}>
      <h4 className="offer-info-column-title">
        {t(`${translations.offers}:basicInfoColumns.Contract`)}
      </h4>
      <SelectInputFieldControlled
        value={formState.contractType}
        label={t(`${translations.offers}:Contract Type`)}
        required
        id="contractType"
        name="contractType"
        onChange={handleFormState}
        options={toOptions(contractTypes)}
        disabled={offerIsLocked()}
      />
      <SelectInputFieldControlled
        value={formState.jobType}
        label={t(`${translations.offers}:Job Type`)}
        required
        id="jobType"
        name="jobType"
        onChange={handleFormState}
        options={toOptions(workTypes)}
        disabled={offerIsLocked()}
      />
      <SelectInputFieldControlled
        value={formState.buildingType}
        // Named as in the offers list; the projects keep "Building Type"
        label={t(`${translations.offers}:list.Building type`)}
        required
        id="buildingType"
        name="buildingType"
        onChange={handleFormState}
        options={toOptions(buildingTypes)}
        disabled={offerIsLocked()}
      />
      {/* How the request came and the information of the employees, not for an
          opportunity */}
      {isOpportunity ? null : (
        <>
          <SelectInputFieldControlled
            value={formState.employeeInformationType}
            label={t(`${translations.offers}:employeeInformationType`)}
            required
            id="employeeInformationType"
            name="employeeInformationType"
            onChange={handleFormState}
            options={toOptions(employeeInformationTypes)}
            disabled={offerIsLocked()}
          />
          <SelectInputFieldControlled
            value={formState.wayOfReceivingType}
            label={t(`${translations.offers}:wayOfReceivingType`)}
            required
            id="wayOfReceivingType"
            name="wayOfReceivingType"
            onChange={handleFormState}
            options={toOptions(wayOfReceivingTypes)}
            disabled={offerIsLocked()}
          />
        </>
      )}
    </Col>
  );

  // The dates of the whole site and of the contract side by side, below the customers,
  // with the department and the times of the offer
  const datesSection = (
    <div className="offer-basic-section">
      <h4 className="offer-info-column-title">
        {t(`${translations.offers}:basicInfoColumns.Schedule`)}
      </h4>
      <div className="offer-dates-grid">
        {/* The times of the offer first, then the dates of the site and the contract */}
        {timeFields}
        <FormGroup>
          <Label className="label">
            {t(`${translations.offers}:Site start`)}
          </Label>
          <DatePicker
            showWeekNumbers
            isClearable={!offerIsLocked()}
            selected={siteStartDate}
            onChange={(date) => onChangeDate(date, 'siteStart')}
            id="siteStartDate"
            disabled={offerIsLocked()}
          />
        </FormGroup>
        <FormGroup>
          <Label className="label">
            {t(`${translations.offers}:Contract start`)}
          </Label>
          <DatePicker
            showWeekNumbers
            selected={startDate}
            onChange={(date) => onChangeDate(date, 'start')}
            id="startDate"
            required
            disabled={offerIsLocked()}
          />
        </FormGroup>
        <FormGroup>
          <Label className="label">
            {t(`${translations.offers}:Contract end`)}
          </Label>
          <DatePicker
            showWeekNumbers
            selected={endDate}
            onChange={(date) => onChangeDate(date, 'end')}
            id="endDate"
            required
            disabled={offerIsLocked()}
          />
        </FormGroup>
        <FormGroup>
          <Label className="label">
            {t(`${translations.offers}:Site end`)}
          </Label>
          <DatePicker
            showWeekNumbers
            isClearable={!offerIsLocked()}
            selected={siteEndDate}
            onChange={(date) => onChangeDate(date, 'siteEnd')}
            id="siteEndDate"
            minDate={siteStartDate ?? undefined}
            disabled={offerIsLocked()}
          />
        </FormGroup>
      </div>
    </div>
  );

  // The collaterals have long names: a line of their own below the long texts
  const collateralField = (
    <Col className="offer-pdf-field offer-pdf-field-wide">
      <SelectInputFieldControlled
        value={formState.collateral}
        label={t(`${translations.offers}:Collateral`)}
        required
        id="collateral"
        name="collateral"
        onChange={handleFormState}
        options={toOptions(collateralTypes)}
        disabled={offerIsLocked()}
      />
    </Col>
  );

  // The terms printed in the information table of the PDF, among its fields
  const termsFields = (
    <>
      <OfferInvoicingInfo
        formState={formState}
        handleFormState={handleFormState}
        handlePaymentTerm={handlePaymentTerm}
        paymentTerm={paymentTerm}
        locked={offerIsLocked()}
      />
      {noCollateral() ? null : (
        <>
          <Col className="offer-pdf-field">
            <NumberInputField
              value={formState.collateralAmount}
              label={t(`${translations.offers}:collateralAmount`)}
              required
              id="collateralAmount"
              name="collateralAmount"
              onChange={handleFormState}
              disabled={offerIsLocked()}
            />
          </Col>
          <Col className="offer-pdf-field">
            <NumberInputField
              value={formState.collateralPercentage}
              label={t(`${translations.offers}:collateralPercentage`)}
              required
              id="collateralPercentage"
              name="collateralPercentage"
              onChange={handleFormState}
              disabled={offerIsLocked()}
            />
          </Col>
        </>
      )}
    </>
  );

  const statusBar = (
    <div className="offer-status-bar">
      {statusFields}
      {offerIsApproved() || readOnly ? null : (
        <PrimaryButton
          className="offer-save-button"
          onClick={() => {
            setSaveAttempted(true);
            saveOffer();
          }}
        >
          {t(`${translations.common}:Save`).toLocaleUpperCase()}
        </PrimaryButton>
      )}
    </div>
  );

  // The customer as a row of company, contact person and task, like the other contacts of
  // the project
  const customerRow = (
    <div className="project-contacts-grid">
      <FormGroup>
        <SearchDropdown
          value={customerOffice}
          required
          placeholder={t(`${translations.common}:Search`)}
          className="text-field search-field"
          name="customerOffice"
          onChange={handleCustomerOffice}
          loadOptions={searchCustomerOffice}
          disabled={offerIsLocked()}
        />
      </FormGroup>
      <SelectInputFieldControlled
        value={formState.customer}
        required
        id="customer"
        name="customer"
        onChange={handleFormState}
        options={customers ? showDesigners(customers) : ''}
        disabled={offerIsLocked()}
      />
      <SelectInputFieldControlled
        value={formState.customerRole ?? ''}
        id="customerRole"
        name="customerRole"
        onChange={handleFormState}
        options={toOptions(customerRoles)}
        disabled={offerIsLocked()}
      />
      <span />
    </div>
  );

  // The other customers of a preliminary offer right below the first one
  const customersSection = (
    <div className="offer-basic-section">
      <h4 className="offer-info-column-title">
        {t(`${translations.offers}:basicInfoColumns.Customers`)}
      </h4>
      <div className="project-contacts-grid project-contacts-header">
        <span>{t(`${translations.offers}:Customer Office`)}</span>
        <span>{t(`${translations.offers}:projectContacts.Person`)}</span>
        <span>{t(`${translations.offers}:projectContacts.Role`)}</span>
        <span />
      </div>
      {customerRow}
      {additionalRecipients}
    </div>
  );

  // A sales opportunity has no separate customers: all the contacts of the project in one
  // list, the customer first
  const contactsSection =
    isOpportunity && projectContacts ? (
      <div className="offer-basic-section">
        {React.cloneElement(projectContacts, { leadingRow: customerRow })}
      </div>
    ) : (
      <div className="offer-basic-section">{projectContacts}</div>
    );

  // The site: its name and the address on one line (street, postal code, city, country).
  // Without labels, each field named by its placeholder and tooltip, so the fields start at
  // the left edge as those of the customers.
  const targetSection = (
    <div className="offer-basic-section offer-target-section">
      <h4 className="offer-info-column-title offer-title-with-actions">
        <span>{t(`${translations.offers}:basicInfoColumns.Target`)}</span>
        {/* An opportunity has no PDF, so its buttons are here */}
        {isOpportunity ? actions : null}
      </h4>
      <FormGroup>
        <Input
          aria-label={t(`${translations.offers}:Target`)}
          className="text-field"
          disabled={offerIsLocked()}
          id="target"
          name="target"
          onChange={handleFormState}
          placeholder={t(`${translations.offers}:Target`)}
          required
          title={t(`${translations.offers}:Target`)}
          type="text"
          value={formState.target ?? ''}
        />
      </FormGroup>
      <FormGroup>
        <div className="offer-address-fields">
          {(
            [
              [['address', 'Address', 'offer-address-street']],
              [
                ['zipCode', 'ZIP Code', 'offer-address-zip'],
                ['city', 'City', 'offer-address-city'],
                ['country', 'Country', 'offer-address-country'],
              ],
            ] as const
          ).map((group) => (
            // The street, then the postal code, city and country kept together
            <div className="offer-address-group" key={group[0][0]}>
              {group.map(([name, labelKey, className]) => (
                <Input
                  aria-label={t(`${translations.offers}:${labelKey}`)}
                  className={`text-field ${className}`}
                  disabled={offerIsLocked()}
                  id={name}
                  key={name}
                  name={name}
                  onChange={handleFormState}
                  placeholder={t(`${translations.offers}:${labelKey}`)}
                  required
                  title={t(`${translations.offers}:${labelKey}`)}
                  type="text"
                  value={formState[name] ?? ''}
                />
              ))}
            </div>
          ))}
        </div>
      </FormGroup>
    </div>
  );

  // A long text of the PDF on a line of its own: the text wraps and the field grows
  const longTextField = (
    name: 'attachmentsNote' | 'cargo' | 'deliveryTimeNote' | 'invoiceNote',
    labelKey: string
  ) => (
    <Col className="offer-pdf-field offer-pdf-field-wide">
      <FormGroup>
        <Label className="label" for={`offer-${name}`}>
          {t(`${translations.offers}:${labelKey}`)}
        </Label>
        <AutoGrowTextArea
          disabled={offerIsLocked()}
          id={`offer-${name}`}
          name={name}
          onChange={handleFormState}
          required
          value={formState[name] ?? ''}
        />
      </FormGroup>
    </Col>
  );
  // The fields of the information table of the PDF and the own rows printed after the
  // collaterals, then the offer text
  const pdfSection = (
    <>
      <h4 className="offer-info-column-title offer-title-with-actions">
        <span>{t(`${translations.offers}:offer`)} PDF</span>
        {actions}
      </h4>
      <div className="offer-pdf-fields offer-pdf-grid">
        {/* The texts on lines of their own, growing with a long text, then the own rows
            of the information table, then the short fields two side by side */}
        {longTextField('attachmentsNote', 'Attachments')}
        {longTextField('cargo', 'Cargo')}
        {longTextField('deliveryTimeNote', 'Delivery time')}
        {longTextField('invoiceNote', 'Invoicing')}
        {collateralField}
        <OfferInfoRowFields
          columnWidth={12}
          disabled={offerIsLocked()}
          onChange={(infoRows) =>
            handleFormState({
              target: { name: 'infoRows', value: infoRows },
            })
          }
          rows={formState.infoRows ?? []}
        />
        {/* The validity and the payment term stand out in green */}
        <Col className="offer-pdf-field offer-pdf-field-key">
          {/* A date from the calendar or free text, e.g. 30 pv */}
          <TextWithDateInputField
            value={formState.validity}
            label={t(`${translations.offers}:Validity`)}
            required
            name="validity"
            onChange={handleFormState}
            disabled={offerIsLocked()}
          />
        </Col>
        {termsFields}
      </div>
      <div className="offer-pdf-text">
        <OfferTextField
          label={t(`${translations.calculationWindow}:Offer text`)}
          locked={offerIsLocked()}
          onChange={(offerText) =>
            handleFormState({
              target: { name: 'offerText', value: offerText },
            })
          }
          value={formState.offerText}
        />
      </div>
    </>
  );

  return (
    <>
      {/* The buttons on the line of the title while the information is closed */}
      <div className="offer-basic-header">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div onClick={toggleOffer} className="header-text title-spacing">
          {offerIsOpen ? (
            <img src={minimize} alt="toggle-offer" className="collapse-icon" />
          ) : (
            <img src={maximize} alt="toggle-offer" className="collapse-icon" />
          )}
          {t(`${translations.offers}:Basic Info`).toUpperCase()}
          {/* An opportunity is named in the title, as its type is not shown */}
          {isOpportunity
            ? ` – ${t(
                `${translations.offers}:types.${OPPORTUNITY}`
              ).toUpperCase()}`
            : null}
        </div>
        {offerIsOpen ? null : actions}
      </div>
      <Collapse isOpen={offerIsOpen}>
        {/* After the first save the fields left empty are shown in light red */}
        <Form className={emptyFieldsClass}>
          {/* The page in two halves separated by a lime line: at the left the
              information of the offer (the site, the customers and the other contacts as
              rows, then the small details in columns), at the right the PDF fields and
              the offer text. An opportunity has no PDF. */}
          <div className={`offer-basic-split ${isOpportunity ? 'single' : ''}`}>
            <div className="offer-basic-left">
              {targetSection}
              {isOpportunity ? contactsSection : customersSection}
              {datesSection}
              {isOpportunity ? null : contactsSection}
              <Row className="offer-info-columns">
                {/* An opportunity has no offer type to change, requested date or language
                    of the PDF, but the phase of its building project */}
                {isOpportunity ? projectPhaseColumn : offerColumn}
                {contractColumn}
              </Row>
            </div>
            {isOpportunity ? null : (
              <div className="offer-basic-right">{pdfSection}</div>
            )}
          </div>
          {/* The department, signer and status on the line of the save button; of a
              sales opportunity at the bottom of the page, in the place given for it */}
          {isOpportunity ? null : statusBar}
        </Form>
        {isOpportunity && statusBarTarget
          ? createPortal(statusBar, statusBarTarget)
          : null}
      </Collapse>
    </>
  );
};

export default OfferBasicInfo;
